import React, { useState, useEffect, useMemo, useContext, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { GlobalContext } from '../../../context/GlobalContext';
import useApi from '../../../hooks/useApi';
import ReportLayout from '../core/ReportLayout';
import ReportKPICards from '../core/ReportKPICards';
import DataTable from '../shared/DataTable';
import { 
  Trophy, 
  TrendingUp, 
  Users, 
  Target, 
  Award,
  ChevronDown,
  Calendar,
  BarChart3,
  PieChart
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell
} from 'recharts';
import { formatCurrency, formatPercentage } from '../shared/utils';
import { exportToPDF, exportToExcel, prepareEmailData } from '../shared/exportUtils';
import BranchDropdown from '../../../components/BranchDropdown';

const AdvisorReport = () => {
  const { uinfo } = useContext(GlobalContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    branch: 'all',
    period: 'month',
    view: 'leaderboard'
  });
  const [selectedBranch, setSelectedBranch] = useState('all');
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [selectedRepFilter, setSelectedRepFilter] = useState(null);
  const lastGoodDataRef = useRef(null); // Preserve last good processed data

  // Get list of all advisors/operators
  const { data: operators, isLoading: operatorsLoading } = useApi({ 
    url: '/salesassist/cplusassist/getOperators', 
    initial: [],
    enabled: true
  });

  // Get branches for dropdown
  const { data: branches, isLoading: branchesLoading } = useApi({ 
    url: '/salesassist/cplusassist/branches', 
    initial: [],
    enabled: true
  });

  // Get operator performance data (has actual sales values, not just "today")
  const { data: performanceData, isLoading: performanceLoading } = useApi({ 
    url: '/visiondashboard/operatorPerformance', 
    initial: [],
    enabled: true
  });

  // Get today's performance data (fallback, but has zeros if no sales today)
  const { data: todayPerformanceData, isLoading: todayLoading } = useApi({ 
    url: '/salesassist/cplusassist/salesMarginByBranchAndInitsToday', 
    initial: [],
    enabled: true
  });

  // Read rep filter from URL query parameter on mount
  useEffect(() => {
    const repParam = searchParams.get('rep');
    if (repParam) {
      const decodedRep = decodeURIComponent(repParam);
      setSelectedRepFilter(decodedRep);
    } else {
      setSelectedRepFilter(null);
    }
  }, [searchParams]);

  // Process data directly in useMemo - simple like InvoiceSummary
  const processedData = useMemo(() => {
    // Don't process if still loading - use last good data if available, otherwise return zeros
    if (operatorsLoading || performanceLoading || todayLoading || branchesLoading) {
      if (lastGoodDataRef.current) {
        return lastGoodDataRef.current; // Return preserved data during loading
      }
      return {
        kpis: [
          { key: 'total-advisors', label: 'Active Advisors', value: 0, type: 'advisors', format: 'number' },
          { key: 'total-sales', label: 'Total Sales', value: 0, type: 'sales', format: 'currency' },
          { key: 'total-margin', label: 'Total Margin', value: 0, type: 'margin', format: 'currency' },
          { key: 'avg-sales', label: 'Avg Sales/Advisor', value: 0, type: 'sales', format: 'currency' }
        ],
        leaderboard: [],
        chartData: [],
        branchData: [],
        tableData: []
      };
    }

    // Extract data from API responses - useApi returns data directly, not wrapped
    const safeOperators = Array.isArray(operators) ? operators : [];
    const safePerformanceData = Array.isArray(performanceData) ? performanceData : [];
    const safeTodayData = Array.isArray(todayPerformanceData) ? todayPerformanceData : [];
    
    // Debug logging
    console.log('AdvisorReport - Data check:', {
      operatorsLength: safeOperators.length,
      performanceDataLength: safePerformanceData.length,
      todayDataLength: safeTodayData.length,
      sampleOperator: safeOperators[0],
      samplePerformance: safePerformanceData[0],
      sampleToday: safeTodayData[0]
    });
    
    // Use performanceData (has actual sales) as primary, fallback to todayPerformanceData
    const dataToUse = safePerformanceData.length > 0 ? safePerformanceData : safeTodayData;
    
    // If no operators at all, we can't process anything
    if (!safeOperators || safeOperators.length === 0) {
      console.warn('AdvisorReport - No operators found, returning empty');
      return {
        kpis: [
          { key: 'total-advisors', label: 'Active Advisors', value: 0, type: 'advisors', format: 'number' },
          { key: 'total-sales', label: 'Total Sales', value: 0, type: 'sales', format: 'currency' },
          { key: 'total-margin', label: 'Total Margin', value: 0, type: 'margin', format: 'currency' },
          { key: 'avg-sales', label: 'Avg Sales/Advisor', value: 0, type: 'sales', format: 'currency' }
        ],
        leaderboard: [],
        chartData: [],
        branchData: [],
        tableData: []
      };
    }
    
    // Log what we're working with
    console.log('AdvisorReport - Processing with:', {
      operatorsCount: safeOperators.length,
      performanceDataCount: safePerformanceData.length,
      todayDataCount: safeTodayData.length,
      salesStaffCount: 0 // Will be calculated below
    });

    // Filter to only include sales staff (not drivers, etc.) and those with valid initials
    const salesStaff = safeOperators.filter(operator => 
      operator.Inits && 
      operator.Inits !== 'N/A' && 
      operator.Status !== 'driver' &&
      (operator.Position?.toLowerCase().includes('sales') || 
       operator.Position?.toLowerCase().includes('advisor') ||
       operator.Position?.toLowerCase().includes('rep') ||
       operator.Status === 'sales' ||
       operator.Status === 'rep')
    );
    
    console.log('AdvisorReport - Sales staff filtered:', {
      totalOperators: safeOperators.length,
      salesStaffCount: salesStaff.length,
      sampleSalesStaff: salesStaff[0]
    });

    // Process advisor data
    const advisorData = salesStaff.map(operator => {
      // Match by _id (which is the initials in performanceData), Inits, or inits
      let performanceData = safePerformanceData.find(item => 
        item._id === operator.Inits ||  // Performance data uses _id as initials!
        item.Inits === operator.Inits || 
        item.inits === operator.Inits ||
        item._id?.toUpperCase() === operator.Inits?.toUpperCase() ||
        item.Inits?.toUpperCase() === operator.Inits?.toUpperCase() ||
        item.inits?.toUpperCase() === operator.Inits?.toUpperCase()
      );
      
      // Fallback to todayPerformanceData if not found in performanceData
      let todayData = null;
      if (!performanceData) {
        todayData = safeTodayData.find(item => 
          item.Inits === operator.Inits || 
          item.inits === operator.Inits ||
          item.Inits?.toUpperCase() === operator.Inits?.toUpperCase() ||
          item.inits?.toUpperCase() === operator.Inits?.toUpperCase()
        );
      }
      
      // Use performanceData if found, otherwise todayData
      const dataSource = performanceData || todayData || {};

      // Performance data has month-to-date values (invoiceValueMonth) which are more useful than today (which are zeros)
      // Parse string values to numbers (API returns strings like "48.47000000")
      const salesValue = performanceData ? 
        parseFloat(performanceData.invoiceValueMonth || performanceData.invoiceValueToday || 0) :
        (parseFloat(dataSource.totalSales) || 
         parseFloat(dataSource.invoiceValueToday) || 
         parseFloat(dataSource.salesToday) || 
         parseFloat(dataSource.sales) || 
         parseFloat(dataSource.invoiceValue) || 0);
      
      const invoiceCount = performanceData ?
        parseInt(performanceData.invoiceCountMonth || performanceData.invoiceCountToday || 0) :
        (parseInt(dataSource.invoicesToday) || 
         parseInt(dataSource.invoices) || 
         parseInt(dataSource.invoiceCount) || 
         parseInt(dataSource.count) || 0);
      
      const marginValue = performanceData ?
        parseFloat(performanceData.marginMonth || performanceData.marginToday || 0) :
        (parseFloat(dataSource.totalMargin) || 
         parseFloat(dataSource.marginToday) || 
         parseFloat(dataSource.margin) || 
         parseFloat(dataSource.marginValue) || 0);
      
      // Calculate margin percent if we have sales and margin
      let marginPercent = 0;
      if (performanceData && performanceData.marginPercentMonth) {
        marginPercent = parseFloat(performanceData.marginPercentMonth);
      } else if (salesValue > 0 && marginValue !== 0) {
        marginPercent = (marginValue / salesValue) * 100;
      } else {
        marginPercent = parseFloat(dataSource.marginPercent || 
                                  dataSource.marginPercentage || 
                                  dataSource.marginPercentToday || 0);
      }

      return {
        advisor: operator.Inits,
        name: `${operator.FName || ''} ${operator.SName || ''}`.trim() || operator.Name || 'Unknown',
        branch: (performanceData?.OpBranch || performanceData?.Branch || dataSource.branch || operator.Branch || 'Unknown'),
        todaySales: salesValue,
        todayInvoices: invoiceCount,
        todayMargin: marginValue,
        todayMarginPercent: marginPercent,
        weeklyTotal: salesValue,
        weeklyInvoices: invoiceCount
      };
    });

    // Filter by selected branch
    let filteredAdvisors = advisorData;
    if (selectedBranch !== 'all') {
      filteredAdvisors = advisorData.filter(advisor => 
        advisor.branch === selectedBranch
      );
    }

    // Filter by rep name/initials if specified from query parameter
    if (selectedRepFilter) {
      filteredAdvisors = filteredAdvisors.filter(advisor => {
        const advisorName = (advisor.name || '').toString().toUpperCase();
        const advisorInits = (advisor.advisor || '').toString().toUpperCase();
        const filterRep = selectedRepFilter.toUpperCase();
        return advisorName === filterRep || 
               advisorInits === filterRep || 
               advisorName.includes(filterRep) || 
               filterRep.includes(advisorName) ||
               advisorInits.includes(filterRep) ||
               filterRep.includes(advisorInits);
      });
    }

    // Sort by sales for leaderboard
    const sortedAdvisors = [...filteredAdvisors].sort((a, b) => 
      (b.todaySales || 0) - (a.todaySales || 0)
    );

    // Top 5 leaderboard
    const leaderboard = sortedAdvisors.slice(0, 5).map((advisor, index) => ({
      rank: index + 1,
      advisor: advisor.advisor,
      name: advisor.name,
      sales: advisor.todaySales || 0,
      branch: advisor.branch || 'Unknown',
      margin: advisor.todayMargin || 0,
      marginPercent: advisor.todayMarginPercent || 0
    }));

    // Chart data for performance comparison
    const chartData = sortedAdvisors.slice(0, 10).map(advisor => ({
      name: advisor.advisor,
      sales: advisor.todaySales || 0,
      margin: advisor.todayMargin || 0,
      invoices: advisor.todayInvoices || 0
    }));

    // Branch performance data - use month-to-date data from performanceData
    const branchData = [];
    if (safePerformanceData && Array.isArray(safePerformanceData) && safePerformanceData.length > 0) {
      const branchMap = {};
      safePerformanceData.forEach(item => {
        // Use OpBranch or Branch field, prefer month data over today
        const branchName = item.OpBranch || item.Branch || item.branch || 'Unknown Branch';
        // Use month-to-date data (has actual values) instead of today (all zeros)
        const salesValue = parseFloat(item.invoiceValueMonth || item.invoiceValueToday || item.salesMonth || item.salesToday || item.totalSales || 0);
        
        if (branchMap[branchName]) {
          branchMap[branchName] += salesValue;
        } else {
          branchMap[branchName] = salesValue;
        }
      });
      
      Object.entries(branchMap)
        .map(([name, value]) => ({ branch: name, totalSales: value }))
        .filter(b => b.totalSales > 0) // Only include branches with sales
        .sort((a, b) => b.totalSales - a.totalSales)
        .forEach(branch => {
          branchData.push(branch);
        });
    }

    // Calculate totals for KPIs - use ALL advisors (not filtered) for totals
    const allAdvisorsTotals = advisorData.reduce((acc, advisor) => {
      acc.totalSales += advisor.todaySales || 0;
      acc.totalInvoices += advisor.todayInvoices || 0;
      acc.totalAdvisors += 1;
      acc.totalMargin += advisor.todayMargin || 0;
      return acc;
    }, {
      totalSales: 0,
      totalInvoices: 0,
      totalAdvisors: 0,
      totalMargin: 0
    });

    const kpis = [
      {
        key: 'total-advisors',
        label: 'Active Advisors',
        value: allAdvisorsTotals.totalAdvisors,
        type: 'advisors',
        format: 'number'
      },
      {
        key: 'total-sales',
        label: 'Total Sales',
        value: allAdvisorsTotals.totalSales,
        type: 'sales',
        format: 'currency'
      },
      {
        key: 'total-margin',
        label: 'Total Margin',
        value: allAdvisorsTotals.totalMargin,
        type: 'margin',
        format: 'currency'
      },
      {
        key: 'avg-sales',
        label: 'Avg Sales/Advisor',
        value: allAdvisorsTotals.totalAdvisors > 0 ? allAdvisorsTotals.totalSales / allAdvisorsTotals.totalAdvisors : 0,
        type: 'sales',
        format: 'currency'
      }
    ];

    // Table data
    const tableData = filteredAdvisors.map(advisor => ({
      advisor: advisor.advisor || 'N/A',
      name: advisor.name || 'N/A',
      branch: advisor.branch || 'Unknown',
      todaySales: advisor.todaySales || 0,
      todayInvoices: advisor.todayInvoices || 0,
      margin: advisor.todayMargin || 0,
      marginPercent: advisor.todayMarginPercent || 0
    }));

    const result = { kpis, leaderboard, chartData, branchData, tableData };
    
    // Log the final result for debugging
    console.log('AdvisorReport - Final processed data:', {
      kpis,
      leaderboardLength: leaderboard.length,
      chartDataLength: chartData.length,
      branchDataLength: branchData.length,
      tableDataLength: tableData.length,
      totalSales: allAdvisorsTotals.totalSales,
      totalAdvisors: allAdvisorsTotals.totalAdvisors
    });
    
    // Always return the processed result - don't block it with preservation logic
    // Only preserve if we actually have sales data
    if (allAdvisorsTotals.totalSales > 0 || allAdvisorsTotals.totalAdvisors > 0) {
      lastGoodDataRef.current = result;
    }
    
    return result;
  }, [operators, performanceData, todayPerformanceData, selectedBranch, selectedRepFilter, operatorsLoading, performanceLoading, todayLoading, branchesLoading]);

  // Check if data is loading
  const isLoading = operatorsLoading || todayLoading || branchesLoading;

  // Export functions
  const handleExportPDF = () => {
    exportToPDF({
      title: 'Advisor Performance Report',
      tableData: processedData.tableData,
      columns: [
        { header: 'Advisor', key: 'advisor' },
        { header: 'Name', key: 'name' },
        { header: 'Branch', key: 'branch' },
        { header: 'Sales', key: 'todaySales', format: 'currency' },
        { header: 'Invoices', key: 'todayInvoices' },
        { header: 'Margin', key: 'margin', format: 'currency' },
        { header: 'Margin %', key: 'marginPercent', format: 'percentage' }
      ],
      kpis: processedData.kpis
    });
  };

  const handleExportExcel = () => {
    exportToExcel(processedData.tableData, {
      title: 'Advisor Performance Report',
      columns: [
        { header: 'Advisor', key: 'advisor' },
        { header: 'Name', key: 'name' },
        { header: 'Branch', key: 'branch' },
        { header: 'Sales', key: 'todaySales', format: 'currency' },
        { header: 'Invoices', key: 'todayInvoices' },
        { header: 'Margin', key: 'margin', format: 'currency' },
        { header: 'Margin %', key: 'marginPercent', format: 'percentage' }
      ],
      kpis: processedData.kpis
    });
  };

  const handleEmailReport = () => {
    const emailData = prepareEmailData({
      title: 'Advisor Performance Report',
      data: processedData.tableData,
      kpis: processedData.kpis
    });
    console.log('Email data prepared:', emailData);
  };

  const handleClearRepFilter = () => {
    setSelectedRepFilter(null);
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('rep');
    setSearchParams(newParams, { replace: true });
  };

  // Table columns
  const columns = [
    {
      key: 'advisor',
      label: 'Advisor Code',
      sortable: true,
      render: (value) => (
        <span className="font-medium text-blue-600">{value}</span>
      )
    },
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      render: (value) => (
        <span className="text-gray-900">{value}</span>
      )
    },
    {
      key: 'branch',
      label: 'Branch',
      sortable: true,
      render: (value) => (
        <span className="text-gray-700">{value}</span>
      )
    },
    {
      key: 'todaySales',
      label: 'Today Sales',
      sortable: true,
      type: 'currency',
      render: (value) => (
        <span className="font-semibold text-gray-900">{formatCurrency(value)}</span>
      )
    },
    {
      key: 'todayInvoices',
      label: 'Invoices',
      sortable: true,
      render: (value) => (
        <span className="text-gray-700">{value}</span>
      )
    },
    {
      key: 'margin',
      label: 'Margin',
      sortable: true,
      type: 'currency',
      render: (value) => (
        <span className="font-semibold text-gray-900">{formatCurrency(value)}</span>
      )
    },
    {
      key: 'marginPercent',
      label: 'Margin %',
      sortable: true,
      type: 'percentage',
      render: (value) => (
        <span className="text-gray-700">{formatPercentage(value)}</span>
      )
    }
  ];

  return (
    <ReportLayout
      title="Advisor Performance Report"
      subtitle="Interactive advisor performance analysis with leaderboards and charts"
      icon={Trophy}
      iconColor="orange"
      onExportPDF={handleExportPDF}
      onExportExcel={handleExportExcel}
      onEmailReport={handleEmailReport}
      rightActions={
        selectedRepFilter ? (
          <button
            onClick={handleClearRepFilter}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Clear Rep Filter
          </button>
        ) : null
      }
    >
      {/* Show loading state while data is loading */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mb-4"></div>
            <p className="text-gray-600">Loading advisor data...</p>
          </div>
        </div>
      ) : (
        <div className="space-y-8">
        {/* Rep Filter Indicator */}
        {selectedRepFilter && (
          <div className="mb-4 px-4 py-2 bg-indigo-50 border border-indigo-200 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-sm text-indigo-800">
                Showing data for rep: <strong>{selectedRepFilter}</strong>
                {processedData.leaderboard.length === 0 && (
                  <span className="ml-2 text-indigo-600">(No matching advisors found)</span>
                )}
              </span>
              <button
                onClick={handleClearRepFilter}
                className="text-sm text-indigo-600 hover:text-indigo-800 underline"
              >
                Show all advisors
              </button>
            </div>
          </div>
        )}
        {/* KPI Cards */}
        <ReportKPICards kpis={processedData.kpis} loading={isLoading} />

        {/* Top 5 Advisors Leaderboard */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <Trophy className="h-6 w-6 text-amber-500" />
              <h2 className="text-xl font-bold text-gray-900">Top 5 Advisors</h2>
            </div>
            <div className="text-sm text-gray-500">
              {selectedBranch === 'all' ? 'All Branches' : selectedBranch}
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {processedData.leaderboard.map((advisor, index) => (
              <div
                key={advisor.advisor}
                className={`p-4 rounded-lg border-2 ${
                  index === 0 ? 'border-amber-400 bg-amber-50' :
                  index === 1 ? 'border-gray-300 bg-gray-50' :
                  index === 2 ? 'border-orange-300 bg-orange-50' :
                  'border-gray-200 bg-white'
                }`}
              >
                <div className="text-center">
                  <div className={`text-2xl font-bold ${
                    index === 0 ? 'text-amber-600' :
                    index === 1 ? 'text-gray-600' :
                    index === 2 ? 'text-orange-600' :
                    'text-gray-500'
                  }`}>
                    #{advisor.rank}
                  </div>
                  <div className="text-sm font-semibold text-gray-900 mt-1">
                    {advisor.name}
                  </div>
                  <div className="text-xs text-gray-600 mb-2">
                    {advisor.advisor}
                  </div>
                  <div className="text-lg font-bold text-green-600">
                    {formatCurrency(advisor.sales)}
                  </div>
                  <div className="text-xs text-gray-500">
                    {formatPercentage(advisor.marginPercent)} margin
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Performance Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Sales Performance Chart */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center space-x-2 mb-4">
              <BarChart3 className="h-5 w-5 text-blue-600" />
              <h3 className="text-lg font-semibold text-gray-900">Top Advisors Sales</h3>
            </div>
            <div style={{ width: '100%', height: 300 }}>
              <ResponsiveContainer>
                <BarChart data={processedData.chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                  <YAxis tickFormatter={(value) => `£${(value/1000).toFixed(0)}k`} />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Bar dataKey="sales" fill="#3b82f6" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Branch Distribution Pie Chart */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center space-x-2 mb-4">
              <PieChart className="h-5 w-5 text-green-600" />
              <h3 className="text-lg font-semibold text-gray-900">Sales by Branch</h3>
            </div>
            <div style={{ width: '100%', height: 300 }}>
              <ResponsiveContainer>
                <RechartsPieChart>
                  <Pie
                    data={processedData.branchData}
                    dataKey="totalSales"
                    nameKey="branch"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={({ branch, totalSales }) => `${branch}: ${formatCurrency(totalSales)}`}
                  >
                    {processedData.branchData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={['#3b82f6', '#10b981', '#f59e0b'][index % 3]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                </RechartsPieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Advisor Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <Users className="h-5 w-5 text-purple-600" />
              <h3 className="text-lg font-semibold text-gray-900">Advisor Performance</h3>
            </div>
          </div>
          <DataTable
            data={processedData.tableData}
            columns={columns}
            loading={isLoading}
            searchable={true}
            sortable={true}
            pagination={true}
            pageSize={10}
            emptyMessage="No advisor data available"
            searchPlaceholder="Search advisors..."
          />
        </div>
        </div>
      )}
    </ReportLayout>
  );
};

export default AdvisorReport;
